import { Skeleton } from "./skeleton";
import { Button } from "./button";
import { cn } from "../lib/utils";

interface LoadingStateProps {
  /**
   * Number of skeleton rows to render. BOUNDED — replaces infinite spinners with
   * a finite, layout-stable placeholder (UI-SPEC §Per-Surface, D-04). Default 3.
   */
  rows?: number;
  /** Optional className for the skeleton container. */
  className?: string;
  /** Accessible label for the busy region. Defaults to "Loading…"; i18n-aware
   * consumers (the client wrapper) pass a translated string. */
  label?: string;
}

/**
 * Bounded loading placeholder (08.7-02, UI-SPEC §Per-Surface). Renders a finite
 * set of skeleton rows inside a polite live region instead of an infinite
 * spinner. Label-injected (D-20): no i18n dependency; the consuming app supplies
 * the translated label (see client/src/core/components/LoadingState wrapper).
 */
export function LoadingState({
  rows = 3,
  className,
  label = "Loading…",
}: LoadingStateProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label}
      className={cn("space-y-3", className)}
      data-slot="loading-state"
    >
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
      <span className="sr-only">{label}</span>
    </div>
  );
}

interface ErrorStateProps {
  /** Already-translated error heading/body. */
  message: string;
  /** Retry handler; when provided a retry button is rendered. */
  onRetry?: () => void;
  /** Retry-button label. Defaults to "Retry"; i18n-aware consumers pass a translated string. */
  retryLabel?: string;
  /** Disable the retry button (e.g. while a retry is in flight). */
  pending?: boolean;
  className?: string;
}

/**
 * Error → retry fallback paired with LoadingState (UI-SPEC §Error states). For
 * REAL failures only — never for unprovisioned infra (that is an EmptyState).
 */
export function ErrorState({
  message,
  onRetry,
  retryLabel = "Retry",
  pending = false,
  className,
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed px-6 py-12 text-center",
        className,
      )}
      data-slot="error-state"
    >
      <p className="text-muted-foreground max-w-sm text-sm text-pretty">
        {message}
      </p>
      {onRetry && (
        <Button variant="outline" onClick={onRetry} pending={pending}>
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

export type { LoadingStateProps, ErrorStateProps };
